import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import Button from "../../../common/inputs/Button";
import { OrgUser } from "../../../../services/api/users";
import {
  AnlageOption,
  AntragVorbelegung,
  LookupOption,
  ProcedureTypeOption,
  StandortmerkmalOption,
  WindenergiegebietOption,
  UvpStatusOption,
} from "../../../../types";
import AntragFelder, {
  AntragFeld,
  AntragFeldHandler,
  AntragFeldWerte,
} from "./AntragFelder";
import { ANTRAG_FELDER } from "./antragsFelder";

interface VerfahrenFormularProps {
  /** Die vorab gewählte Antragsart – im Formular nicht mehr änderbar */
  procedureTypeId: string;
  disabled?: boolean;
  procedureTypeOptions: ProcedureTypeOption[];
  uvpStatusOptions: UvpStatusOption[];
  vorhabenbestandteilOptions: LookupOption[];
  /** Die Anlagen des Projekts; welche vorgewählt sind, sagt die Vorbelegung */
  anlageOptionen: AnlageOption[];
  windenergiegebietOptions: WindenergiegebietOption[];
  standortmerkmalOptions: StandortmerkmalOption[];
  /** Benutzer des Mandanten – Auswahl für die Bearbeitung */
  orgUsers: OrgUser[];
  /** IAM-ID des angemeldeten Benutzers – Vorbelegung der Bearbeitung */
  aktuellerBenutzerId?: string;
  /** Projekt des neuen Antrags – gruppiert die Bezugsanträge nach eigenem und fremdem Projekt */
  projectId?: string;
  /**
   * Womit das Formular startet – vom Server nach der Regel `regeln/vorbelegung_antrag.py`
   * (Antragsart und UVP-Status aus der Anlagenzahl, Standort vom zuletzt angelegten Antrag …).
   */
  vorbelegung: AntragVorbelegung | null;
  /**
   * UVP-Status für eine geänderte Anlagenauswahl – dieselbe Regel, mit der Zahl der gewählten
   * Anlagen gefragt. Das Formular rechnet die Schwellen nicht selbst nach (ADR 008).
   */
  uvpVorbelegungFuer: (anlagenzahl: number) => Promise<string | null>;
  submitLabel?: string;
  onSubmit: (werte: AntragFeldWerte) => void;
  /** Abbrechen – nur vorhanden, wenn das Formular in einem Dialog steckt */
  onCancel?: () => void;
}

const KEIN_WERT = "";

/**
 * Anlegen eines Antrags – dieselben Abschnitte wie die Stammdaten des geöffneten Antrags
 * (`AntragFelder`), beschränkt auf das, was beim Anlegen schon feststeht.
 *
 * Dasselbe Formular erscheint an zwei Stellen: direkt auf der Seite, solange kein Antrag
 * existiert, und im Dialog, sobald einer da ist. Es gibt es bewusst nur einmal.
 *
 * Die Vorbelegung kommt vom Server; hier steht nur die Prüfung beim Absenden. Die Felder selbst
 * kennt nur `AntragFelder`.
 */
const VerfahrenFormular: React.FC<VerfahrenFormularProps> = ({
  procedureTypeId,
  disabled = false,
  procedureTypeOptions,
  uvpStatusOptions,
  vorhabenbestandteilOptions,
  anlageOptionen,
  windenergiegebietOptions,
  standortmerkmalOptions,
  orgUsers,
  aktuellerBenutzerId,
  projectId,
  vorbelegung,
  uvpVorbelegungFuer,
  submitLabel = "Anlegen",
  onSubmit,
  onCancel,
}) => {
  const [werte, setWerte] = useState<AntragFeldWerte>(() => ({
    procedureTypeId,
    bezeichnung: "",
    bearbeitungUserId: orgUsers.some((u) => u.user_id === aktuellerBenutzerId)
      ? (aktuellerBenutzerId ?? KEIN_WERT)
      : KEIN_WERT,
    bezugsgenehmigungId: KEIN_WERT,
    zusatzantragBemerkung: "",
    beschreibung: "",
    anlageIds: vorbelegung?.anlage_ids ?? [],
    vorhabenbestandteilIds: vorbelegung?.vorhabenbestandteil_ids ?? [],
    uvpStatusId: vorbelegung?.uvp_status_id ?? KEIN_WERT,
    windenergiegebietId: vorbelegung?.windenergiegebiet_id ?? KEIN_WERT,
    standortmerkmalIds: vorbelegung?.standortmerkmal_ids ?? [],
    eingereichtAm: "",
    vollstaendigSeit: "",
    genehmigungGeplantAm: vorbelegung?.genehmigung_geplant_am ?? "",
    conferencePlannedOn: "",
    bescheidVom: "",
    aktenzeichen: "",
  }));

  /**
   * Der UVP-Status zieht mit der Anlagenauswahl mit, solange der Nutzer ihn nicht selbst gesetzt
   * hat. Ohne Anlagen im Projekt gibt es keine Auswahl – dann bleibt die Vorbelegung stehen.
   */
  const [uvpManuell, setUvpManuell] = useState(false);
  const anzahlGewaehlt = werte.anlageIds.length;
  // Beim ersten Lauf gilt die Vorbelegung schon – sie ist mit genau dieser Auswahl entstanden.
  const gestartet = useRef(false);
  useEffect(() => {
    if (!gestartet.current) {
      gestartet.current = true;
      return;
    }
    if (uvpManuell || anlageOptionen.length === 0) return;
    let aktuell = true;
    void uvpVorbelegungFuer(anzahlGewaehlt).then((uvpStatusId) => {
      if (aktuell) {
        setWerte((bisher) => ({
          ...bisher,
          uvpStatusId: uvpStatusId ?? KEIN_WERT,
        }));
      }
    });
    return () => {
      aktuell = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anzahlGewaehlt, uvpManuell]);

  const aendere: AntragFeldHandler = (feld, wert) => {
    if (feld === "uvpStatusId") setUvpManuell(true);
    setWerte((bisher) => ({ ...bisher, [feld]: wert }));
  };

  /**
   * Fehler erst beim Absenden ermitteln und anzeigen, nicht laufend – der Button bleibt bis
   * dahin klickbar (wie im Anlagen-Dialog), statt grau und rätselhaft deaktiviert zu sein.
   */
  const [formErrors, setFormErrors] = useState<
    Partial<Record<AntragFeld, string>>
  >({});

  const validateForm = (): boolean => {
    const errors: Partial<Record<AntragFeld, string>> = {};
    if (!werte.bezeichnung.trim())
      errors.bezeichnung = "Bezeichnung ist erforderlich";
    if (werte.bearbeitungUserId === KEIN_WERT) {
      errors.bearbeitungUserId = "Bearbeitung ist erforderlich";
    }
    // Ohne Anlagen im Projekt gibt es nichts zu wählen – dann ist das Feld keine Pflicht.
    if (anlageOptionen.length > 0 && werte.anlageIds.length === 0) {
      errors.anlageIds = `${ANTRAG_FELDER.anlageIds.label} sind erforderlich`;
    }
    if (!werte.genehmigungGeplantAm) {
      errors.genehmigungGeplantAm = `${ANTRAG_FELDER.genehmigungGeplantAm.label} ist erforderlich`;
    }
    if (werte.uvpStatusId === KEIN_WERT) {
      errors.uvpStatusId = "UVP-Status ist erforderlich";
    }
    if (werte.vorhabenbestandteilIds.length === 0) {
      errors.vorhabenbestandteilIds = `${ANTRAG_FELDER.vorhabenbestandteilIds.label} ist erforderlich`;
    }
    // Ohne die Lage lässt sich fachlich nicht beurteilen, welche Antragsbausteine der Antrag
    // braucht.
    if (werte.windenergiegebietId === KEIN_WERT) {
      errors.windenergiegebietId = `${ANTRAG_FELDER.windenergiegebietId.label} ist erforderlich`;
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    onSubmit({
      ...werte,
      bezeichnung: werte.bezeichnung.trim(),
      beschreibung: werte.beschreibung.trim(),
    });
  };

  return (
    <Box>
      <AntragFelder
        variante="anlegen"
        werte={werte}
        onChange={aendere}
        fehler={formErrors}
        disabled={disabled}
        procedureTypeOptions={procedureTypeOptions}
        uvpStatusOptions={uvpStatusOptions}
        vorhabenbestandteilOptions={vorhabenbestandteilOptions}
        anlageOptionen={anlageOptionen}
        windenergiegebietOptions={windenergiegebietOptions}
        standortmerkmalOptions={standortmerkmalOptions}
        orgUsers={orgUsers}
        projectId={projectId}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        {onCancel && (
          <Button variant="secondary" disabled={disabled} onClick={onCancel}>
            Abbrechen
          </Button>
        )}
        <Button variant="primary" disabled={disabled} onClick={handleSubmit}>
          {submitLabel}
        </Button>
      </Box>
    </Box>
  );
};

export default VerfahrenFormular;
